import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';

/**
 * Internal utility for converting OBJ garment assets to GLB format
 * Used by the AR pipeline for loading 3D clothing models
 */
export class GarmentAssetConverter {
    private scene: THREE.Scene;
    private exporter: GLTFExporter;
    private objLoader: OBJLoader;
    private mtlLoader: MTLLoader;

    constructor() {
        this.scene = new THREE.Scene();
        this.exporter = new GLTFExporter();
        this.objLoader = new OBJLoader();
        this.mtlLoader = new MTLLoader();
    }

    /**
     * Convert OBJ garment asset to GLB format
     * @param objFile - The OBJ file to convert
     * @param mtlFile - Optional MTL material file
     * @param textureFiles - Optional texture files
     * @returns Promise<ArrayBuffer> - GLB data
     */
    async convertGarmentAsset(
        objFile: File,
        mtlFile: File | null = null,
        textureFiles: File[] = []
    ): Promise<ArrayBuffer> {
        try {
            // Clear previous scene
            while (this.scene.children.length > 0) {
                this.scene.remove(this.scene.children[0]);
            }

            // Load MTL file if provided
            if (mtlFile) {
                const materials = await this.loadMTL(mtlFile);
                this.objLoader.setMaterials(materials);
            }

            // Load OBJ file
            const object = await this.loadOBJ(objFile);

            // Add object to scene
            this.scene.add(object);

            // Load and apply textures if provided
            if (textureFiles.length > 0) {
                await this.applyTextures(object, textureFiles);
            }

            // Optimize the mesh for AR use
            this.optimizeForAR(object);

            // Export to GLB
            const glbData = await this.exportToGLB();

            return glbData;

        } catch (error) {
            console.error('Garment asset conversion failed:', error);
            throw error;
        }
    }

    loadMTL(mtlFile: File): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const contents = event.target?.result as string;
                const materials = this.mtlLoader.parse(contents, '');
                resolve(materials);
            };
            reader.onerror = reject;
            reader.readAsText(mtlFile);
        });
    }

    loadOBJ(objFile: File): Promise<THREE.Object3D> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const contents = event.target?.result as string;
                const object = this.objLoader.parse(contents);
                resolve(object);
            };
            reader.onerror = reject;
            reader.readAsText(objFile);
        });
    }

    async applyTextures(object: THREE.Object3D, textureFiles: File[]) {
        const textureMap = new Map<string, THREE.Texture>();

        // Load all textures
        for (const file of textureFiles) {
            const texture = await this.loadTexture(file);
            textureMap.set(file.name.toLowerCase(), texture);
        }

        // Apply textures to materials
        object.traverse((child) => {
            if (child instanceof THREE.Mesh && child.material) {
                const material = child.material as THREE.Material;
                if (material.name) {
                    const textureName = material.name.toLowerCase();
                    const extensions = ['.jpg', '.png', '.jpeg', '.bmp', '.tga'];

                    for (const ext of extensions) {
                        const fullName = textureName + ext;
                        if (textureMap.has(fullName)) {
                            const texture = textureMap.get(fullName)!;
                            (material as THREE.MeshStandardMaterial).map = texture;
                            material.needsUpdate = true;
                            break;
                        }
                    }
                }
            }
        });
    }

    loadTexture(file: File): Promise<THREE.Texture> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const texture = new THREE.Texture(img);
                    texture.needsUpdate = true;
                    resolve(texture);
                };
                img.onerror = reject;
                img.src = event.target?.result as string;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    private optimizeForAR(object: THREE.Object3D) {
        object.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                const geometry = child.geometry;

                // Merge vertices to reduce geometry complexity
                geometry.mergeVertices();

                // Compute vertex normals if missing
                if (!geometry.attributes.normal) {
                    geometry.computeVertexNormals();
                }

                // Compute bounding box and sphere for AR placement
                geometry.computeBoundingBox();
                geometry.computeBoundingSphere();

                // Optimize material for AR rendering
                if (child.material) {
                    const material = child.material as THREE.Material;
                    material.side = THREE.FrontSide; // Ensure proper face culling
                    material.transparent = false; // Disable transparency for performance
                }

                // Scale down if too large (assuming meters in AR)
                const bbox = geometry.boundingBox;
                if (bbox) {
                    const size = new THREE.Vector3();
                    bbox.getSize(size);
                    const maxDimension = Math.max(size.x, size.y, size.z);
                    if (maxDimension > 2) { // If larger than 2 meters, scale down
                        const scale = 2 / maxDimension;
                        child.scale.multiplyScalar(scale);
                    }
                }
            }
        });
    }

    exportToGLB(): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            this.exporter.parse(
                this.scene,
                (result) => {
                    if (result instanceof ArrayBuffer) {
                        resolve(result);
                    } else {
                        // Convert JSON to ArrayBuffer if needed
                        const jsonString = JSON.stringify(result);
                        const buffer = new ArrayBuffer(jsonString.length);
                        const view = new Uint8Array(buffer);
                        for (let i = 0; i < jsonString.length; i++) {
                            view[i] = jsonString.charCodeAt(i);
                        }
                        resolve(buffer);
                    }
                },
                (error) => {
                    reject(error);
                },
                { binary: true }
            );
        });
    }

    /**
     * Save GLB data to a file (for internal use)
     */
    saveGLB(glbData: ArrayBuffer, filename: string) {
        const blob = new Blob([glbData], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
}

// Alias so legacy imports of 'OBJToGLBConverter' still work
export { GarmentAssetConverter as OBJToGLBConverter };